import * as R from "ramda";
import React, { useEffect, useState } from "react";
import * as Fa from "react-icons/fa";
import { NavLink as NavigationLink, NavLinkProps } from "react-router-dom";
import ToolTip from "react-tooltip";
import styled from "styled-components";
import { IObjectsAmount, ObjectAmount } from "../../../App/actions";
import { api } from "../../../dependencies";
import PackageIcon from "../Icons/PackageIcon";
import {
  LinkWrapper,
  ObjectAmountContainer,
  OrgLabel,
  SideMenuContainer,
  SideMenuTopContainer,
} from "./SideMenu.styles";

interface ISideMenuOwnProps {
  expanded: boolean;
  userName: string;
  organizationName: string;
  isOpen: boolean;
  onClose: () => void;
  renderLinks?: () => JSX.Element;
  courseAmount?: ObjectAmount;
  classAmount?: ObjectAmount;
  groupsAmount?: ObjectAmount;
  usersAmount?: ObjectAmount;
}

export type ISideMenuStateProps = IObjectsAmount & {
  accountId: string;
  isOrgAdmin: boolean;
};

export type SideMenuProps = ISideMenuOwnProps & ISideMenuStateProps;

const GlobeDiv = styled.div`
  display: flex;
  align-items: center;
`;
const ButtonText = styled.span`
  word-wrap: break-word;
  overflow: hidden;
`;
const Separator = styled.div`
  height: 2.5rem;
  display: flex;
  align-items: center;
  -webkit-transition: all 0.5s ease;
  -moz-transition: all 0.5s ease;
  transition: all 0.5s ease;
  overflow: hidden;
`;

const GreyedOutLink = styled(NavigationLink)`
  opacity: 0.5;
`;

const NAY = styled.i`
  font-weight: bold;
`;

interface INAYTooltipContext {
  showTooltip: () => void;
  hideTooltip: () => void;
}

const NAYTooltipContext = React.createContext<INAYTooltipContext>({
  hideTooltip: () => {
    return;
  },
  showTooltip: () => {
    return;
  },
});

const DisabledLink: React.FC<IDisabledLinkProps> = props => {
  const { showTooltip, hideTooltip } = React.useContext(NAYTooltipContext);
  const { icon, desc, amount } = props;

  return (
    <GreyedOutLink to="#" data-for="nay" data-tip="" onMouseEnter={showTooltip} onMouseLeave={hideTooltip}>
      <LinkWrapper>
        {icon}
        <ButtonText>{desc}</ButtonText>
        {!R.isNil(amount) && <ObjectAmountContainer>{amount}</ObjectAmountContainer>}
      </LinkWrapper>
    </GreyedOutLink>
  );
};

interface ISideMenuState {
  canChangeOrg: boolean;
  isLoading: boolean;
}

interface INavLinkProps {
  icon: React.ReactNode;
  desc: string;
  amount?: ObjectAmount;
  heightSVG?: string;
}

interface IDisabledLinkProps {
  icon: React.ReactNode;
  desc: string;
  amount?: ObjectAmount;
}

enum SideMenuOption {
  Classes = "classes-link",
  Scorm = "scorm-link",
  Theme = "theme-link",
  Dashboard = "dashboard-link",
}

export const NavLink: React.FC<INavLinkProps & NavLinkProps> = props => {
  const { icon, desc, amount, heightSVG, ...linkProps } = props;

  return (
    <NavigationLink activeClassName={"selected"} {...linkProps}>
      <LinkWrapper heightSVG={heightSVG}>
        {icon}
        <ButtonText>{desc}</ButtonText>
        {!R.isNil(amount) && <ObjectAmountContainer>{amount}</ObjectAmountContainer>}
      </LinkWrapper>
    </NavigationLink>
  );
};

const NavLinks = (
  onClick: () => void,
  isOpen: boolean,
  isOrgAdmin: boolean,
  { courseAmount, classAmount, exportedScormPackagesAmount, groupsAmount, usersAmount }: IObjectsAmount
) => () => (
  <>
    <NavLink to="/dashboard" id={SideMenuOption.Dashboard} onClick={onClick} icon={<Fa.FaThLarge />} desc="Dashboard" />
    <Separator className={isOpen ? "separator" : "separator-hidden"}>My Platform</Separator>
    <NavLink to="/courses" onClick={onClick} icon={<Fa.FaGraduationCap />} desc={"Courses"} amount={courseAmount} />
    <NavLink
      to="/classes"
      id={SideMenuOption.Classes}
      onClick={onClick}
      icon={<Fa.FaUsers />}
      desc={"Classes"}
      amount={classAmount}
    />
    <NavLink
      to="/exported-scorm-packages"
      id={SideMenuOption.Scorm}
      data-testid="scorm-link"
      onClick={onClick}
      icon={<PackageIcon />}
      desc={"SCORM"}
      amount={exportedScormPackagesAmount}
    />
    <DisabledLink icon={<Fa.FaUserFriends />} desc={"Groups"} amount={groupsAmount} />
    {isOrgAdmin && (
      <>
        <Separator className={isOpen ? "separator" : "separator-hidden"}>My Organisation</Separator>
        <NavLink
          to="/accounts"
          onClick={onClick}
          icon={<Fa.FaUser />}
          heightSVG="2rem"
          desc={"Accounts"}
          amount={usersAmount}
        />
        <NavLink to="/organization-settings" onClick={onClick} icon={<Fa.FaCog />} desc={"Settings"} />
        <NavLink to="/themes" id={SideMenuOption.Theme} onClick={onClick} icon={<Fa.FaPalette />} desc={"Themes"} />
        <DisabledLink icon={<Fa.FaFont />} desc={"Fonts"} />
      </>
    )}
    <Separator className={isOpen ? "separator" : "separator-hidden"}>System</Separator>
    <DisabledLink icon={<PackageIcon />} desc={"Client Packages"} />
    <DisabledLink icon={<PackageIcon />} desc={"SCORM Packages"} />
  </>
);

export const SideMenuComponent: React.FC<SideMenuProps> = props => {
  const {
    accountId,
    expanded,
    organizationName,
    onClose,
    classAmount,
    courseAmount,
    exportedScormPackagesAmount,
    groupsAmount,
    usersAmount,
    renderLinks,
    isOpen,
    isOrgAdmin,
  } = props;
  const ref = React.useRef<HTMLDivElement>(null);
  const [showTooltip, toggleShowTooltip] = React.useState(false);
  const [state, setState] = useState<ISideMenuState>({
    canChangeOrg: false,
    isLoading: true,
  });
  const toggleTooltip = (value: boolean) => () => toggleShowTooltip(value);
  useEffect(() => {
    api.organizations.getAdministrableBy(accountId).then(response => {
      const [error, res] = response;

      if (!error && res && res.data && res.data.length > 0) {
        return setState({ isLoading: false, canChangeOrg: true });
      }

      setState({ ...state, isLoading: false });
    });
  }, [state.isLoading]);

  useEffect(() => {
    const checkIfClickedOutside = (e: Event) => {
      if (expanded && ref.current && !ref.current.contains(e.target as HTMLElement)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", checkIfClickedOutside);

    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [expanded]);

  const getLinks = renderLinks
    ? renderLinks
    : NavLinks(onClose, isOpen, isOrgAdmin, {
        classAmount,
        courseAmount,
        exportedScormPackagesAmount,
        groupsAmount,
        usersAmount,
      });

  const OrgName = () => {
    return (
      <LinkWrapper>
        <GlobeDiv>
          <Fa.FaGlobe />
        </GlobeDiv>
        <ButtonText>{state.isLoading ? "Loading..." : organizationName}</ButtonText>
      </LinkWrapper>
    );
  };

  return (
    <SideMenuContainer className={isOpen ? undefined : "collapsed"} expanded={expanded} ref={ref}>
      <SideMenuTopContainer>
        {state.canChangeOrg ? (
          <NavigationLink
            to="/select-organization"
            onClick={onClose}
            className={"select-organization"}
            activeClassName={"selected"}
          >
            <OrgName />
          </NavigationLink>
        ) : (
          <OrgLabel>
            <OrgName />
          </OrgLabel>
        )}
        <NAYTooltipContext.Provider
          value={{
            hideTooltip: toggleTooltip(false),
            showTooltip: toggleTooltip(true),
          }}
        >
          {getLinks()}
        </NAYTooltipContext.Provider>
      </SideMenuTopContainer>
      <ToolTip id="nay" place="bottom">
        {showTooltip && <NAY>Not available yet.</NAY>}
      </ToolTip>
    </SideMenuContainer>
  );
};
